import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { z } from 'zod'
import { useAuth } from '@/contexts/AuthContext'
import { PageTitle } from '@/components/PageTitle'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Fade } from '@/components/animate-ui/primitives/effects/fade'
import { Slide } from '@/components/animate-ui/primitives/effects/slide'

const loginSearchSchema = z.object({
  redirect: z.string().optional(),
})

export const Route = createFileRoute('/_auth/login/')({
  validateSearch: loginSearchSchema,
  component: LoginPage,
})

// Google Icon SVG
function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  )
}

function LoginPage() {
  const { loginWithProvider, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const { redirect: redirectParam } = Route.useSearch()

  // Redirect if already authenticated, honoring the redirect search param
  useEffect(() => {
    if (!isAuthenticated) return

    if (redirectParam) {
      try {
        // Full URL — extract the path to avoid a hard reload
        const url = new URL(redirectParam)
        if (url.origin === window.location.origin) {
          void navigate({ to: (url.pathname + url.search + url.hash) as '/' })
          return
        }
      } catch {
        // Not a full URL — treat as a path
        void navigate({ to: redirectParam as '/' })
        return
      }
    }

    void navigate({ to: '/home' })
  }, [isAuthenticated, navigate, redirectParam])

  return (
    <>
      <PageTitle title="Sign In" />
      <Fade inView delay={300}>
        <Card className="w-full border-border/50 shadow-lg">
          <CardHeader className="space-y-3 pb-6 pt-8">
            <Slide direction="up" inView delay={400}>
              <CardTitle className="text-3xl font-bold">Sign In</CardTitle>
            </Slide>
            <Slide direction="up" inView delay={500}>
              <CardDescription className="text-base">
                Choose your preferred sign-in method
              </CardDescription>
            </Slide>
          </CardHeader>
          <CardContent className="pb-8">
            <Slide direction="up" inView delay={600}>
              <Button
                onClick={() => {
                  const basePath = import.meta.env.VITE_BASE_PATH || ''
                  const origin = window.location.origin

                  let destinationPath = '/home'
                  if (redirectParam) {
                    try {
                      const url = new URL(redirectParam)
                      if (url.origin === origin) {
                        destinationPath = url.pathname + url.search + url.hash
                      }
                    } catch {
                      destinationPath = redirectParam.startsWith('/')
                        ? redirectParam
                        : `/${redirectParam}`
                    }
                  }

                  const returnUrl = origin + basePath + destinationPath
                  loginWithProvider('google', returnUrl)
                }}
                className="w-full h-14 text-base font-medium bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-900 dark:text-white border-2 border-gray-200 dark:border-gray-600 shadow-sm transition-all hover:shadow-md hover:scale-[1.02]"
              >
                <GoogleIcon />
                Sign in with Google
              </Button>
            </Slide>
          </CardContent>
        </Card>
      </Fade>
    </>
  )
}
